/**
 * Catchball Ball Possession RPC Client
 *
 * Typed wrappers for the catchball_claim_ball / catchball_release_ball RPCs
 * defined in migration 578_catchball_ball_possession.sql. Mirrors
 * itemProvenanceRpc.ts's style: thin async functions, p_-prefixed SQL param
 * names, and a { success, error? } return shape.
 *
 * The server derives the effective actor from auth.uid() and only falls back
 * to p_actor_id for service-role/test callers, so a spoofed actorId cannot
 * escalate — these wrappers pass the client's best-known user id for that
 * fallback and for parity with the other catchball RPC callers.
 */

import { getSupabaseClient } from './supabaseClient';

export interface BallPossessionRpcResult {
  success: boolean;
  error?: string;
}

interface BallPossessionRpcResponse {
  success?: boolean;
  error?: string;
}

function toResult(
  data: unknown,
  rpcError: { message: string } | null,
  fallbackError: string,
): BallPossessionRpcResult {
  if (rpcError) return { success: false, error: rpcError.message };

  const result = data as BallPossessionRpcResponse | null;
  if (!result) return { success: false, error: 'No response from server' };
  if (!result.success) return { success: false, error: result.error ?? fallbackError };

  return { success: true };
}

/**
 * Grab the ball on a loop. Server-side (catchball_claim_ball RPC) rejects
 * when the loop is closed, the ball is already held by someone else, or the
 * caller is not eligible at the ball's current tier.
 */
export async function claimBallRpc(
  loopId: string,
  actorId: string,
): Promise<BallPossessionRpcResult> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('catchball_claim_ball', {
      p_loop_id: loopId,
      p_actor_id: actorId,
    });
    return toResult(data, error, 'Claim failed');
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Claim failed' };
  }
}

/**
 * Release the ball on a loop. Server-side (catchball_release_ball RPC) the
 * holder may always release; cycle owner / super admin / workspace admin may
 * override. Rejects when the ball is not currently held or the caller is
 * neither the holder nor an authorized overrider.
 */
export async function releaseBallRpc(
  loopId: string,
  actorId: string,
): Promise<BallPossessionRpcResult> {
  try {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase.rpc('catchball_release_ball', {
      p_loop_id: loopId,
      p_actor_id: actorId,
    });
    return toResult(data, error, 'Release failed');
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Release failed' };
  }
}
